// The four-criterion priority score for one opportunity (Praxis Scoring Model Scope, v0.7): what Praxis
// recommends per criterion and why, NTN's override with its reason, the weighted total, the band and the
// flags. For a client login only the band, the total, and the effective Applicability and Magnitude show.
import { useState } from 'react';
import { api } from '../lib/api';
import type { Band, Criterion, Decision, MatchScores, Weights } from '../lib/api';
import { BAND_LABEL, BandPill, DECISION_LABEL } from './matches';

const CRITERIA: Criterion[] = ['applicability', 'access', 'magnitude', 'complexity'];
const LABEL: Record<Criterion, string> = { applicability: 'Applicability', access: 'Access', magnitude: 'Magnitude', complexity: 'Complexity' };
const HINT: Record<Criterion, string> = {
  applicability: 'alignment with capabilities and strategic priorities',
  access: 'relationships with the decision makers (5 = direct, 1 = none)',
  magnitude: 'identifiable funding and scale (5 = confirmed, 1 = unknown)',
  complexity: 'ease of execution (5 = easy, 1 = major obstacles)',
};
const FLAG_LABEL: Record<string, string> = {
  applicability_low: 'Applicability 1', access_low: 'No access', magnitude_low: 'No funding identified', complexity_low: 'Very high complexity',
  applicability_unknown: 'Applicability unknown', access_unknown: 'Relationships unknown', magnitude_unknown: 'Funding unknown', complexity_unknown: 'Complexity unknown',
};
const BAND_DECISION: Record<Band, Decision | null> = { high: 'pursue', medium: 'relevant', monitor: 'watch', low: null };

const eff = (x: MatchScores, c: Criterion): number | null => (x[`${c}_set`] !== null ? x[`${c}_set`] : x[`${c}_rec`]);

export function ScorePanel({ matchId, scores, weights, canEdit, clientView, decision, onChanged }:
  { matchId: string; scores: MatchScores | null; weights: Weights; canEdit: boolean; clientView: boolean; decision: Decision; onChanged: () => void }) {
  const [editing, setEditing] = useState<Criterion | null>(null);
  const [value, setValue] = useState<string>('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!scores) return <div className="muted sm">Not scored yet. The priority score is computed on the next ingest or profile edit.</div>;
  const x = scores;
  const rows = clientView ? CRITERIA.filter((c) => c === 'applicability' || c === 'magnitude') : CRITERIA;

  function startEdit(c: Criterion) {
    setEditing(c);
    setValue(x[`${c}_set`] === null ? '' : String(x[`${c}_set`]));
    setWhy(x[`${c}_set_why`] ?? '');
    setErr(null);
  }
  async function save(c: Criterion) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/matches/${matchId}/scores`, { method: 'PATCH', json: { criterion: c, value: value === '' ? null : Number(value), why: why.trim() || undefined } });
      setEditing(null);
      onChanged();
    } catch (e) { setErr((e as Error).message === 'reason_required' ? 'Give a reason for the override.' : (e as Error).message); } finally { setBusy(false); }
  }

  const suggested = x.band ? BAND_DECISION[x.band] : null;
  return (
    <>
      <table className="data-table score-table">
        <thead><tr><th>Criterion</th><th className="num">Weight</th><th>{clientView ? 'Score' : 'Praxis recommends'}</th>{!clientView && <th>NTN</th>}<th className="num">Points</th></tr></thead>
        <tbody>
          {rows.map((c) => {
            const rec = x[`${c}_rec`], set = x[`${c}_set`], e = eff(x, c);
            const pts = Math.round(((e ?? 1) / 5) * weights[c] * 10) / 10;
            return (
              <tr key={c}>
                <td><strong>{LABEL[c]}</strong><div className="why">{HINT[c]}</div></td>
                <td className="num">{weights[c]}%</td>
                <td>
                  <span className="val">{rec === null ? '—' : rec}</span>{rec !== null && <span className="muted sm"> / 5</span>}
                  <div className="why">{x[`${c}_rec_why`] ?? (clientView ? 'Not yet assessed.' : 'No recommendation.')}</div>
                </td>
                {!clientView && (
                  <td>
                    {editing === c ? (
                      <div>
                        <div className="score-override">
                          <select className="select" value={value} onChange={(ev) => setValue(ev.target.value)} aria-label={`${LABEL[c]} override`}>
                            <option value="">Use Praxis{rec !== null ? ` (${rec})` : ''}</option>
                            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                          <input className="input" value={why} onChange={(ev) => setWhy(ev.target.value)} placeholder={value === '' ? 'Reason (optional when clearing)' : 'Reason for the override'} aria-label="Reason" />
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <button type="button" className="primary-btn" disabled={busy || (value !== '' && !why.trim())} onClick={() => save(c)}>{busy ? 'Saving…' : 'Save'}</button>
                            <button type="button" className="ghost-btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                          </span>
                        </div>
                        {err && <div className="error-text sm">{err}</div>}
                      </div>
                    ) : set !== null ? (
                      <>
                        <span className="val">{set}</span><span className="muted sm"> / 5 · override</span>
                        <div className="why">{x[`${c}_set_why`]}</div>
                        {canEdit && <button type="button" className="link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => startEdit(c)}>Change</button>}
                      </>
                    ) : canEdit ? (
                      <button type="button" className="ghost-btn" onClick={() => startEdit(c)}>{rec === null ? 'Set' : 'Override'}</button>
                    ) : <span className="muted sm">{rec === null ? 'not set' : 'as recommended'}</span>}
                  </td>
                )}
                <td className="num">{e === null ? <span className="muted" title="Unknown counts as 1">{pts}</span> : pts}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="score-total">
        <span className="big">{x.total === null ? '—' : Math.round(x.total)}</span>
        <span className="muted sm">/ 100</span>
        <BandPill band={x.band} />
        {x.flags.map((f) => <span key={f} className="score-flag" title="A criterion at 1, or unknown, is flagged">{FLAG_LABEL[f] ?? f}</span>)}
        {!clientView && x.band && (
          <span className="muted sm">
            {suggested ? `${BAND_LABEL[x.band]} suggests ${DECISION_LABEL[suggested]}${decision === suggested ? ' (current)' : decision !== 'pending' ? `; current decision is ${DECISION_LABEL[decision]}` : ''}.` : 'Low priority: stays visible, deprioritised unless new information emerges.'}
          </span>
        )}
        {!clientView && x.overridden_by_name && x.overridden_at && <span className="muted sm">Overrides by {x.overridden_by_name}, {new Date(x.overridden_at).toLocaleDateString()}.</span>}
      </div>
    </>
  );
}
